import React, { useMemo } from "react";

import { useQuery } from "@apollo/client";
import { Add, OpenInFull } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { formatISO } from "date-fns/esm";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";

import { ContainedButton, OutlinedButton } from "@/components/Button";
import { Spacer } from "@/components/Spacer/Spacer";
import { Body, Title3Bold } from "@/components/Typography";
import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import NewClientDialog from "@/pages/Clients/ClientList/components/NewClientDialog";
import { colors } from "@/theme/overrides/colors";

import ClientItem from "./ClientItem";
import pinnedClients from "./store/PinnedClients";
import {
  sortByClientNameAsc,
  sortByClientNameDesc,
  sortByPinnedPrimarily,
  sortByStatus,
} from "./utils/sort";
import { useSidebarContext } from "../../Sidebar";
import HoverBase from "../ClickBase";

const clientListQuery = graphql(`
  query ClientList($currentDate: ISO8601Date!) {
    clients {
      nodes {
        id
        profile {
          id
          fullName
          avatarUrl(variant: THUMB)
        }
        programForDate(date: $currentDate) {
          id
          name
        }
        status
      }
    }
  }
`);

const ClientList: React.FC = observer(() => {
  const { dispatch } = useSidebarContext();
  const currentDate = formatISO(new Date(), { representation: "date" });
  const { data, refetch } = useQuery(clientListQuery, {
    variables: { currentDate: currentDate },
  });
  const clients = data?.clients.nodes ?? [];
  const [sortValue, setSortValue] =
    React.useState<"status" | "asc" | "desc">("status");
  const [searchText, setSearchText] = React.useState("");
  const addClientDialog = useDialogState();

  function handleSort(e: SelectChangeEvent) {
    const sort = e.target.value as "status" | "asc" | "desc";
    setSortValue(sort);
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchText(event.target.value);
  }

  const allClients = useMemo(() => {
    const secondarySort = {
      status: sortByStatus,
      asc: sortByClientNameAsc,
      desc: sortByClientNameDesc,
    }[sortValue];
    return [...clients]
      .filter((client) => {
        const search = searchText.toLowerCase();
        return client.profile?.fullName.toLowerCase().includes(search);
      })
      .sort(sortByPinnedPrimarily(secondarySort));
  }, [clients, sortValue, searchText, pinnedClients.size]);

  const navigate = useNavigate();
  return (
    <>
      <HoverBase componentName="clients">
        <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Title3Bold text="Clients" />
            <Box display="flex">
              <OutlinedButton
                text="Expand"
                startIcon={<OpenInFull />}
                onClick={() => {
                  dispatch({ type: "close-component" });
                  navigate("/clients");
                }}
              />
              <Spacer w={8} />
              <ContainedButton
                text="Add new Client"
                startIcon={<Add />}
                onClick={() => {
                  dispatch({ type: "close-component" });
                  addClientDialog.show();
                }}
              />
            </Box>
          </Box>
          <Spacer h={24} />
          <Box display="flex">
            <Box>
              <TextField
                value={searchText}
                onChange={handleSearch}
                fullWidth
                size="small"
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon htmlColor={colors.gray} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Spacer w={10} />
            <Box width="40%">
              <Select
                value={sortValue}
                inputProps={{ "aria-label": "Without label" }}
                onChange={handleSort}
                variant="standard"
                fullWidth
              >
                <MenuItem value={""} disabled>
                  Sort by
                </MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </Box>
          </Box>
          <Spacer h={24} />
          <Box flex={1}>
            {allClients?.length === 0 ? (
              <Body text="No user found" />
            ) : (
              <>
                <Virtuoso
                  data={allClients}
                  itemContent={(index, client) => (
                    <>
                      {!pinnedClients.has(client.id) &&
                        pinnedClients.has(
                          allClients[Math.max(index - 1, 0)].id
                        ) && <Divider sx={{ mx: 2 }} />}
                      <ClientItem client={client} />
                    </>
                  )}
                />
              </>
            )}
          </Box>
        </Box>
      </HoverBase>
      <NewClientDialog
        {...addClientDialog.props}
        afterCreate={async () => {
          await refetch();
          addClientDialog.hide();
        }}
      />
    </>
  );
});

export default ClientList;
